import type { QueryClient } from '@tanstack/react-query';
import { hashKey, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addCartProduct,
  addLoyaltyRedemption,
  applyPromoCode,
  checkout,
  createCart,
  deleteCart,
  removeCartProduct,
  removeLoyaltyRedemption,
  updateCart,
  updateCartProduct,
} from './cart.api';
import { cartKeys } from './cart.queries';
import type {
  AddCartProductProps,
  AddCartProductResponse,
  AddLoyaltyRedemptionProps,
  ApplyPromoCodeProps,
  Cart,
  CheckoutProps,
  CheckoutResponse,
  CreateCartProps,
  DeleteCartProps,
  DeliveryAddressParams,
  RemoveCartProductProps,
  RemoveLoyaltyRedemptionProps,
  UpdateCartProductProps,
  UpdateCartProps,
} from './cart.types';

const noop = () => {};

/**
 * Writes the response to one key and marks that uuid's other entries (address,
 * payment method) stale. Only observed ones refetch.
 */
const setCartQueryData = (
  queryClient: QueryClient,
  cart: Cart,
  deliveryAddress?: DeliveryAddressParams
) => {
  const writtenKey = cartKeys.detail(cart.uuid, undefined, deliveryAddress);
  const writtenHash = hashKey(writtenKey);

  queryClient.setQueryData(writtenKey, cart);
  queryClient.invalidateQueries({
    predicate: (query) => query.queryHash !== writtenHash,
    queryKey: cartKeys.detail(cart.uuid),
  });
};

/**
 * A mutation's own `isPending` cannot cross hook instances, so every cart write
 * carries a key that `useIsMutating` can filter on. All ten share the `['cart']`
 * prefix, which is what makes `all` match any cart mutation in flight.
 */
export const cartMutationKeys = {
  all: ['cart'],
  addLoyaltyRedemption: ['cart', 'addLoyaltyRedemption'],
  addProduct: ['cart', 'addProduct'],
  applyPromoCode: ['cart', 'applyPromoCode'],
  checkout: ['cart', 'checkout'],
  create: ['cart', 'create'],
  delete: ['cart', 'delete'],
  removeLoyaltyRedemption: ['cart', 'removeLoyaltyRedemption'],
  removeProduct: ['cart', 'removeProduct'],
  update: ['cart', 'update'],
  updateProduct: ['cart', 'updateProduct'],
} as const;

interface MutationCallbacks<TData = unknown, TVariables = unknown> {
  onError?: (error: Error, variables: TVariables) => void;
  onSuccess?: (data: TData, variables: TVariables) => void;
}

export const useCreateCart = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<Cart, CreateCartProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCart,
    mutationKey: cartMutationKeys.create,
    onError,
    onSuccess: (cart, variables) => {
      setCartQueryData(queryClient, cart);
      onSuccess(cart, variables);
    },
  });
};

export const useUpdateCart = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<Cart, UpdateCartProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCart,
    mutationKey: cartMutationKeys.update,
    onError,
    onSuccess: (cart, variables) => {
      // Keyed on the address this PATCH sent
      setCartQueryData(queryClient, cart, variables.deliveryAddress);
      onSuccess(cart, variables);
    },
  });
};

export const useDeleteCart = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<void, DeleteCartProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCart,
    mutationKey: cartMutationKeys.delete,
    onError,
    onSuccess: (_, variables) => {
      queryClient.removeQueries({ queryKey: cartKeys.detail(variables.uuid) });
      onSuccess(undefined, variables);
    },
  });
};

export const useAddCartProduct = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<AddCartProductResponse, AddCartProductProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addCartProduct,
    mutationKey: cartMutationKeys.addProduct,
    onError,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cartKeys.detail(data.cart_uuid),
      });
      onSuccess(data, variables);
    },
  });
};

export const useUpdateCartProduct = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<void, UpdateCartProductProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateCartProductProps) => {
      await updateCartProduct(variables);
    },
    mutationKey: cartMutationKeys.updateProduct,
    onError,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cartKeys.detail(variables.cartUuid),
      });
      onSuccess(undefined, variables);
    },
  });
};

export const useRemoveCartProduct = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<void, RemoveCartProductProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: RemoveCartProductProps) => {
      await removeCartProduct(variables);
    },
    mutationKey: cartMutationKeys.removeProduct,
    onError,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cartKeys.detail(variables.cartUuid),
      });
      onSuccess(undefined, variables);
    },
  });
};

export const useCheckout = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<CheckoutResponse, CheckoutProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: checkout,
    mutationKey: cartMutationKeys.checkout,
    onError,
    onSuccess: (data, variables) => {
      queryClient.removeQueries({
        queryKey: cartKeys.detail(variables.cartUuid),
      });
      onSuccess(data, variables);
    },
  });
};

export const useApplyPromoCode = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<Cart, ApplyPromoCodeProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyPromoCode,
    mutationKey: cartMutationKeys.applyPromoCode,
    onError,
    onSuccess: (cart, variables) => {
      setCartQueryData(queryClient, cart);
      onSuccess(cart, variables);
    },
  });
};

export const useAddLoyaltyRedemption = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<Cart, AddLoyaltyRedemptionProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addLoyaltyRedemption,
    mutationKey: cartMutationKeys.addLoyaltyRedemption,
    onError,
    onSuccess: (cart, variables) => {
      setCartQueryData(queryClient, cart);
      onSuccess(cart, variables);
    },
  });
};

export const useRemoveLoyaltyRedemption = ({
  onSuccess = noop,
  onError = noop,
}: MutationCallbacks<void, RemoveLoyaltyRedemptionProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: RemoveLoyaltyRedemptionProps) => {
      await removeLoyaltyRedemption(variables);
    },
    mutationKey: cartMutationKeys.removeLoyaltyRedemption,
    onError,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cartKeys.detail(variables.cartUuid),
      });
      onSuccess(undefined, variables);
    },
  });
};
